import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

/** Owns the WebGLRenderer, tone mapping, shadows, environment and resize. */
export class Renderer {
  readonly three: THREE.WebGLRenderer;

  constructor(canvas: HTMLCanvasElement) {
    // Opaque canvas: the browser compositor can use its fast path instead of
    // alpha-blending the WebGL layer against the page every frame. (Used to be
    // alpha:true for a CSS3D YouTube overlay on the hall's TV screen — removed
    // for the compositing + video-decode cost of a live video running the whole
    // time you're at the table.)
    this.three = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
    // Cap pixel ratio: on HiDPI/4K, 2× means ~4× the pixels to shade every frame.
    // 1.5 keeps it crisp at a big fill-rate saving.
    this.three.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    // Neutral (Khronos PBR) tone mapping keeps material colours saturated; ACES
    // Filmic noticeably greys/desaturates the felt and balls.
    this.three.toneMapping = THREE.NeutralToneMapping;
    this.three.toneMappingExposure = 0.82;
    this.three.outputColorSpace = THREE.SRGBColorSpace;
    this.three.shadowMap.enabled = true;
    this.three.shadowMap.type = THREE.PCFSoftShadowMap;
    // Don't recompute shadow maps every frame — the table/room shadows are static.
    // The app flags shadowMap.needsUpdate only on frames where geometry moves
    // (balls rolling, cue aiming, scene rebuilt). One initial pass below.
    this.three.shadowMap.autoUpdate = false;
    this.three.shadowMap.needsUpdate = true;
  }

  /** Generate a neutral indoor IBL so the phenolic balls get realistic reflections. */
  makeEnvironment(): THREE.Texture {
    const pmrem = new THREE.PMREMGenerator(this.three);
    const env = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();
    return env;
  }

  resize(width: number, height: number): void {
    this.three.setSize(width, height, false);
  }

  render(scene: THREE.Scene, camera: THREE.Camera): void {
    this.three.render(scene, camera);
  }
}
